import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactFlow, { 
  Background, 
  Controls,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Download, Copy, Eye, Database } from 'lucide-react';
import { toast } from 'sonner';
import { TableNode } from '../components/TableNode';
import { CustomOrthogonalEdge } from '../components/CustomOrthogonalEdge';
import { DSLEditor } from '../components/DSLEditor';

const API = process.env.REACT_APP_BACKEND_URL;

const nodeTypes = {
  tableNode: TableNode,
};

const edgeTypes = {
  custom: CustomOrthogonalEdge,
};

export const SharedProjectPage = () => {
  const { shareToken } = useParams();
  const [project, setProject] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportedSQL, setExportedSQL] = useState('');
  const [dslWidth, setDslWidth] = useState(450);
  const reactFlowWrapper = useRef(null);

  useEffect(() => {
    fetchSharedProject();
  }, [shareToken]);

  const fetchSharedProject = async () => {
    try {
      const response = await axios.get(`${API}/api/public/projects/${shareToken}`);
      setProject(response.data);
      
      // Load canvas state
      if (response.data.canvas_state) {
        const canvasState = response.data.canvas_state;
        
        const loadedNodes = canvasState.tables.map(table => ({
          id: table.id,
          type: 'tableNode',
          position: table.position,
          data: {
            id: table.id,
            name: table.name,
            columns: table.columns,
          }
        }));

        const loadedEdges = canvasState.relationships.map(rel => {
          let sourceHandle = rel.source_handle;
          let targetHandle = rel.target_handle;
          
          if (!sourceHandle || !targetHandle) {
            const sourceTable = loadedNodes.find(n => n.id === rel.source_table_id);
            const targetTable = loadedNodes.find(n => n.id === rel.target_table_id);
            
            if (sourceTable && targetTable) {
              const dx = targetTable.position.x - sourceTable.position.x;
              sourceHandle = dx > 0 ? `${rel.source_column_id}-right` : `${rel.source_column_id}-left`;
              targetHandle = dx > 0 ? `${rel.target_column_id}-left` : `${rel.target_column_id}-right`;
            }
          }
          
          return {
            id: rel.id,
            source: rel.source_table_id,
            target: rel.target_table_id,
            sourceHandle,
            targetHandle,
            type: 'custom',
            animated: true,
            style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
            data: rel
          };
        });

        setNodes(loadedNodes);
        setEdges(loadedEdges);
      }
    } catch (error) {
      toast.error('Failed to load shared project. Link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSQL = async () => {
    try {
      const response = await axios.post(`${API}/api/public/projects/${shareToken}/export-sql`);
      setExportedSQL(response.data.sql);
      setShowExportDialog(true);
      toast.success('SQL berhasil di-export!');
    } catch (error) {
      toast.error('Failed to export SQL');
    }
  };

  const handleCopySQL = () => {
    navigator.clipboard.writeText(exportedSQL);
    toast.success('SQL copied to clipboard');
  };

  const handleDownloadSQL = () => {
    const blob = new Blob([exportedSQL], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.name || 'schema'}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('SQL file downloaded');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Database className="w-16 h-16 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-muted-foreground">Loading shared project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Database className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Project Not Found</h2>
          <p className="text-muted-foreground">This shared link is invalid or the project is no longer public.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Eye className="w-5 h-5 text-primary" />
            <div>
              <h1 className="text-xl font-bold">{project.name}</h1>
              <p className="text-xs text-muted-foreground">Read-only view • Shared project</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button onClick={handleExportSQL} variant="default">
              <Download className="w-4 h-4 mr-2" />
              Export SQL
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* DSL Editor - Read Only */}
        <div className="border-r border-border flex-shrink-0" style={{ width: `${dslWidth}px` }}>
          <DSLEditor
            tables={nodes.map(n => ({ id: n.id, name: n.data.name, columns: n.data.columns, position: n.position }))}
            relationships={edges.map(e => e.data)}
            onUpdate={() => {}} // No-op for read-only
            className="w-full h-full"
          />
          <div className="p-3 border-t border-border bg-accent/30">
            <p className="text-xs text-muted-foreground text-center">
              📖 View-only mode • Cannot edit
            </p>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={() => {}} // Disable edge deletion
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{
              animated: true,
              style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 }
            }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            fitView
          >
            <Background color="#aaa" gap={16} />
            <Controls showInteractive={false} />
            
            {/* View-only indicator */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-card/90 backdrop-blur-sm border border-border rounded-lg px-4 py-2 shadow-lg pointer-events-none">
              <div className="flex items-center space-x-2 text-sm">
                <Eye className="w-4 h-4 text-primary" />
                <span className="font-medium">View-Only Mode</span>
                <span className="text-muted-foreground">• Shared by owner</span>
              </div>
            </div>
          </ReactFlow>
        </div>
      </div>

      {/* Export SQL Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Export SQL</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-auto">
            <pre className="bg-accent p-4 rounded text-xs font-mono">
              {exportedSQL}
            </pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCopySQL}>
              <Copy className="w-4 h-4 mr-2" />
              Copy to Clipboard
            </Button>
            <Button onClick={handleDownloadSQL}>
              <Download className="w-4 h-4 mr-2" />
              Download SQL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
